import React from 'react';
import { Helmet } from 'react-helmet-async';
import Header from '../components/Header/Header';
import './BlogPage.css';

/**
 * BlogPage - Professional blog page for mental health clinic
 * Newspaper-inspired layout with premium, minimalist design
 */
const BlogPage: React.FC = () => {
  return (
    <>
      {/* SEO and Meta Information */}
      <Helmet>
        <title>Blog | Conectemos Chile - Reflexiones sobre Salud Mental</title>
        <meta 
          name="description" 
          content="Artículos profesionales sobre salud mental, bienestar psicológico y desarrollo personal escritos por nuestro equipo de especialistas en Conectemos Chile."
        />
        <meta name="keywords" content="salud mental, psicología, bienestar, terapia, autoestima, desarrollo personal" />
        <meta property="og:title" content="Blog | Conectemos Chile - Reflexiones sobre Salud Mental" />
        <meta property="og:description" content="Artículos profesionales sobre salud mental y bienestar psicológico" />
        <meta property="og:type" content="article" />
        <meta property="og:image" content="https://www.conectemoschile.cl/og/blog.jpg?v=1" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://www.conectemoschile.cl/og/blog.jpg?v=1" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://www.conectemoschile.cl/blog" />
      </Helmet>

      {/* Header Component */}
      <Header />

      {/* Main Blog Content */}
      <main className="blog-page">
        {/* Hero Section */}
        <section className="blog-hero">
          <div className="blog-hero__container">
            <div className="blog-hero__content">
              <div className="blog-hero__category">
                <span>Salud Mental</span>
                <time dateTime="2025-03-12">12 de Marzo, 2025</time>
              </div>
              <h1 className="blog-hero__title">
                La Importancia del Autocuidado Mental en el Siglo XXI: 
                <span className="blog-hero__title-accent"> Una Perspectiva Profesional</span>
              </h1>
              <p className="blog-hero__excerpt">
                En un mundo cada vez más acelerado y digitalizado, el autocuidado mental se ha convertido 
                en una necesidad fundamental para mantener el bienestar psicológico y emocional. 
                Exploramos estrategias basadas en evidencia para cultivar una mente saludable.
              </p>
            </div>
          </div>
        </section>

        {/* Article Content */}
        <article className="blog-article">
          <div className="blog-article__container">
            <div className="blog-article__content">
              
              {/* Author Section */}
              <div className="blog-article__author">
                <div className="author-card">
                  <div className="author-card__image">
                    <img
                      src="/assets/img/terapista_angel.webp"
                      alt="Director Conectemos Chile"
                      width="80"
                      height="80"
                    />
                  </div>
                  <div className="author-card__info">
                    <h3 className="author-card__name">Ángel Díaz</h3>
                    <p className="author-card__title">Director & Psicólogo Clínico — Conectemos Chile</p>
                    <p className="author-card__credentials">
                      Especialista en Terapia de Parejas y Salud Mental
                    </p>
                  </div>
                </div>
              </div>

              {/* Article Text */}
              <div className="blog-article__text">
                
                <div className="article-section">
                  <h2>La Crisis Silenciosa de Nuestra Era</h2>
                  <p className="lead-paragraph">
                    En las últimas décadas, hemos presenciado un aumento exponencial en los trastornos 
                    relacionados con la salud mental. La ansiedad, la depresión y el estrés crónico se han 
                    convertido en epidemias silenciosas que afectan a millones de personas en todo el mundo.
                  </p>
                  <p>
                    Como profesionales de la salud mental en <strong>Conectemos Chile</strong>, observamos 
                    diariamente el impacto que el ritmo acelerado de la vida moderna tiene en el bienestar 
                    psicológico de nuestros pacientes. La hiperconectividad digital, las presiones laborales, 
                    la incertidumbre económica y los cambios sociales constantes han creado un cóctel perfecto 
                    para el deterioro de la salud mental.
                  </p>
                </div>

                <div className="article-section">
                  <h2>¿Qué es Realmente el Autocuidado Mental?</h2>
                  <p>
                    El autocuidado mental va mucho más allá de las prácticas superficiales que a menudo 
                    vemos promocionadas en redes sociales. No se trata únicamente de tomar un baño relajante 
                    o practicar mindfulness ocasionalmente, aunque estas actividades pueden ser beneficiosas.
                  </p>
                  
                  <blockquote className="article-quote">
                    "El verdadero autocuidado mental implica desarrollar una relación consciente y 
                    compasiva con nosotros mismos, estableciendo límites saludables y cultivando 
                    hábitos que nutran nuestra estabilidad emocional a largo plazo."
                  </blockquote>

                  <p>
                    Desde una perspectiva clínica, el autocuidado mental abarca varias dimensiones 
                    fundamentales:
                  </p>
                  
                  <ul className="article-list">
                    <li><strong>Autoconciencia emocional:</strong> La capacidad de reconocer, nombrar y comprender nuestras emociones sin juicio.</li>
                    <li><strong>Regulación emocional:</strong> Desarrollar estrategias saludables para gestionar emociones intensas o desafiantes.</li>
                    <li><strong>Establecimiento de límites:</strong> Aprender a decir "no" de manera asertiva y proteger nuestro tiempo y energía.</li>
                    <li><strong>Conexión social significativa:</strong> Cultivar relaciones auténticas que nos aporten apoyo y comprensión.</li>
                    <li><strong>Propósito y significado:</strong> Conectar con valores personales y actividades que generen sentido de trascendencia.</li>
                  </ul>
                </div>

                <div className="article-section">
                  <h2>Estrategias Basadas en Evidencia</h2>
                  <p>
                    En nuestra práctica clínica, hemos identificado cinco pilares fundamentales que 
                    sustentan un programa efectivo de autocuidado mental:
                  </p>

                  <div className="strategy-grid">
                    <div className="strategy-card">
                      <div className="strategy-card__number">01</div>
                      <h3>Práctica de la Atención Plena</h3>
                      <p>
                        La investigación neurocientífica ha demostrado que la práctica regular de mindfulness 
                        puede modificar la estructura del cerebro, fortaleciendo áreas asociadas con la 
                        regulación emocional y reduciendo la actividad en regiones relacionadas con la ansiedad.
                      </p>
                    </div>

                    <div className="strategy-card">
                      <div className="strategy-card__number">02</div>
                      <h3>Higiene del Sueño</h3>
                      <p>
                        Un sueño de calidad es fundamental para la consolidación de la memoria emocional 
                        y la regulación del estado de ánimo. Establecer rutinas consistentes de sueño puede 
                        ser uno de los cambios más impactantes para la salud mental.
                      </p>
                    </div>

                    <div className="strategy-card">
                      <div className="strategy-card__number">03</div>
                      <h3>Actividad Física Regular</h3>
                      <p>
                        El ejercicio no solo beneficia el cuerpo, sino que actúa como un poderoso 
                        antidepresivo natural. La actividad física regular estimula la producción de 
                        endorfinas y neurotransmisores que mejoran el estado de ánimo.
                      </p>
                    </div>

                    <div className="strategy-card">
                      <div className="strategy-card__number">04</div>
                      <h3>Nutrición Consciente</h3>
                      <p>
                        La conexión entre la alimentación y la salud mental es innegable. Una dieta 
                        equilibrada rica en omega-3, vitaminas del complejo B y antioxidantes puede 
                        influir significativamente en el bienestar emocional.
                      </p>
                    </div>

                    <div className="strategy-card">
                      <div className="strategy-card__number">05</div>
                      <h3>Conexión Social Auténtica</h3>
                      <p>
                        Los seres humanos somos inherentemente sociales. Mantener conexiones significativas 
                        y buscar apoyo cuando lo necesitamos es crucial para la resiliencia mental y 
                        emocional a largo plazo.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="article-section">
                  <h2>Cuándo Buscar Ayuda Profesional</h2>
                  <p>
                    Es importante reconocer que el autocuidado mental, aunque fundamental, no siempre es 
                    suficiente. Existen momentos en los que buscar ayuda profesional no solo es recomendable, 
                    sino necesario para nuestro bienestar.
                  </p>
                  
                  <p>
                    En <strong>Conectemos Chile</strong>, creemos firmemente que buscar ayuda terapéutica 
                    es un acto de fortaleza, no de debilidad. Algunas señales que indican la necesidad 
                    de apoyo profesional incluyen:
                  </p>

                  <div className="warning-signs">
                    <div className="warning-sign">
                      <i className="fas fa-exclamation-triangle"></i>
                      <span>Síntomas persistentes de ansiedad o depresión que interfieren con la vida diaria</span>
                    </div>
                    <div className="warning-sign">
                      <i className="fas fa-exclamation-triangle"></i>
                      <span>Dificultades significativas en las relaciones interpersonales</span>
                    </div>
                    <div className="warning-sign">
                      <i className="fas fa-exclamation-triangle"></i>
                      <span>Problemas de sueño crónicos o cambios drásticos en los patrones de sueño</span>
                    </div>
                    <div className="warning-sign">
                      <i className="fas fa-exclamation-triangle"></i>
                      <span>Uso de sustancias como mecanismo de afrontamiento</span>
                    </div>
                    <div className="warning-sign">
                      <i className="fas fa-exclamation-triangle"></i>
                      <span>Pensamientos recurrentes de autolesión o ideación suicida</span>
                    </div>
                  </div>
                </div>

                <div className="article-section">
                  <h2>Un Compromiso con el Bienestar Integral</h2>
                  <p>
                    El autocuidado mental no es un destino, sino un viaje continuo de autoconocimiento 
                    y crecimiento personal. Cada pequeño paso que tomamos hacia el cuidado de nuestra 
                    salud mental es una inversión en nuestra calidad de vida presente y futura.
                  </p>
                  
                  <p>
                    En nuestra clínica, trabajamos con un enfoque integral que combina las mejores 
                    prácticas de la psicología clínica contemporánea con un profundo respeto por la 
                    individualidad de cada persona. Creemos que todos merecen vivir una vida plena, 
                    equilibrada y significativa.
                  </p>

                  <div className="call-to-action">
                    <h3>¿Te sientes identificado con alguno de estos temas?</h3>
                    <p>
                      Nuestro equipo de profesionales especializados está aquí para acompañarte en tu 
                      proceso de bienestar mental. No tienes que enfrentar los desafíos solo.
                    </p>
                    <div className="cta-buttons">
                      <a href="/agendar" className="btn btn-primary">
                        <i className="fas fa-calendar-check"></i>
                        Agendar Consulta
                      </a>
                      <a href="#contact" className="btn btn-secondary">
                        <i className="fas fa-comments"></i>
                        Solicitar Información
                      </a>
                    </div>
                  </div>
                </div>

              </div>

              {/* Article Footer */}
              <footer className="blog-article__footer">
                <div className="article-tags">
                  <span className="tag">#SaludMental</span>
                  <span className="tag">#Autocuidado</span>
                  <span className="tag">#Bienestar</span>
                  <span className="tag">#PsicologíaClínica</span>
                  <span className="tag">#TerapiaPsicológica</span>
                </div>
                
                <div className="article-share">
                  <h4>Comparte este artículo</h4>
                  <div className="share-buttons">
                    <button className="share-btn share-btn--linkedin" aria-label="Compartir en LinkedIn">
                      <i className="fab fa-linkedin-in"></i>
                    </button>
                    <button className="share-btn share-btn--twitter" aria-label="Compartir en Twitter">
                      <i className="fab fa-twitter"></i>
                    </button>
                    <button className="share-btn share-btn--facebook" aria-label="Compartir en Facebook">
                      <i className="fab fa-facebook-f"></i>
                    </button>
                    <button className="share-btn share-btn--whatsapp" aria-label="Compartir en WhatsApp">
                      <i className="fab fa-whatsapp"></i>
                    </button>
                  </div>
                </div>
              </footer>

            </div>
          </div>
        </article>

        {/* Related Articles Section */}
        <section className="blog-related">
          <div className="blog-related__container">
            <h2 className="blog-related__title">Próximos Artículos</h2>
            <p className="blog-related__subtitle">
              Estamos preparando más contenido profesional para ayudarte en tu bienestar mental
            </p>
            
            <div className="related-grid">
              <div className="related-card">
                <div className="related-card__category">Terapia de Pareja</div>
                <h3>Comunicación Efectiva en las Relaciones de Pareja</h3>
                <p>Técnicas profesionales para fortalecer la comunicación y resolver conflictos de manera constructiva.</p>
                <span className="related-card__status">Próximamente</span>
              </div>
              
              <div className="related-card">
                <div className="related-card__category">Psicología Infantil</div>
                <h3>Acompañando a los Niños en el Desarrollo Emocional</h3>
                <p>Estrategias para padres y educadores en el fortalecimiento de la inteligencia emocional en la infancia.</p>
                <span className="related-card__status">Próximamente</span>
              </div>
              
              <div className="related-card">
                <div className="related-card__category">Estrés Laboral</div>
                <h3>Prevención del Burnout: Una Guía Práctica</h3>
                <p>Identificación temprana y estrategias de prevención del agotamiento laboral desde la perspectiva clínica.</p>
                <span className="related-card__status">Próximamente</span>
              </div>
            </div>
          </div>
        </section>

      </main>

      {/* Footer would be added here if you have one */}
    </>
  );
};

export default BlogPage;